import React, { useEffect, useState } from 'react';
import { AlertCircle, X } from 'lucide-react';
import axios from 'axios';

const SubscriptionBanner = () => {
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      // 2026-05-20 fix: skip superadmin endpoint for non-superadmin users (avoid 403 in console)
      let userRole = null;
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        userRole = (payload.role || '').toLowerCase();
      } catch (e) {}
      const isSuperAdmin = userRole === 'super_admin' || userRole === 'superadmin';

      // Fetch subscription status, company info (super_admin only), and reminder settings
      const [subRes, companyRes, reminderRes] = await Promise.all([
        axios.get(`${API_URL}/api/v2/tenant/subscription-status`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => null),
        isSuperAdmin
          ? axios.get(`${API_URL}/api/v2/superadmin/company-info`, {
              headers: { Authorization: `Bearer ${token}` }
            }).catch(() => null)
          : Promise.resolve(null),
        axios.get(`${API_URL}/api/v2/reminder-settings`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => null)
      ]);

      if (subRes?.data?.data) {
        setSubscriptionStatus(subRes.data.data);
      }
      
      // Prioritize reminder settings over company info
      if (reminderRes?.data?.contact) {
        setCompanyInfo({
          company_phone: reminderRes.data.contact.whatsapp_number,
          company_email: reminderRes.data.contact.billing_email
        });
      } else if (companyRes?.data?.data) {
        setCompanyInfo(companyRes.data.data);
      }
    } catch (error) {
      // Silently fail - don't show banner if can't fetch
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !subscriptionStatus || dismissed) {
    return null;
  }

  const { status, days_remaining, grace_period_remaining, subscription } = subscriptionStatus;

  // Don't show banner for superadmin or if active with more than 7 days
  if (status === 'active' || status === 'not_found' || status === 'no_subscription') {
    return null;
  }

  // Get contact info from company settings or use defaults
  const whatsappNumber = companyInfo?.company_phone || '6281234567890';
  const billingEmail = companyInfo?.company_email || 'billing@isp.com';

  // Determine banner config based on status - using inline styles for vibrant colors
  let bannerConfig = {
    bgColor: '#fef9c3', // yellow-200
    borderColor: '#eab308', // yellow-500
    textColor: '#854d0e', // yellow-800
    iconColor: '#ca8a04', // yellow-600
    title: '⚠️ Reminder',
    message: '',
    showDismiss: true
  };

  if (status === 'warning_7') {
    bannerConfig.message = `Langganan Anda akan jatuh tempo dalam ${days_remaining} hari (${subscription?.end_date?.substring(0, 10)}). Silakan perpanjang untuk menghindari gangguan layanan.`;
  } else if (status === 'warning_3') {
    bannerConfig.bgColor = '#fed7aa'; // orange-200
    bannerConfig.borderColor = '#f97316'; // orange-500
    bannerConfig.textColor = '#9a3412'; // orange-800
    bannerConfig.iconColor = '#ea580c'; // orange-600
    bannerConfig.title = '⚠️ Peringatan';
    bannerConfig.message = `URGENT! Langganan Anda akan jatuh tempo dalam ${days_remaining} hari! Perpanjang segera untuk menghindari pemblokiran akun.`;
  } else if (status === 'expired') {
    bannerConfig.bgColor = '#fecaca'; // red-200
    bannerConfig.borderColor = '#ef4444'; // red-500
    bannerConfig.textColor = '#991b1b'; // red-800
    bannerConfig.iconColor = '#dc2626'; // red-600
    bannerConfig.title = '🚨 Langganan Habis';
    bannerConfig.message = `Langganan Anda telah HABIS! Grace period: ${grace_period_remaining} hari tersisa. Akun akan diblokir setelah grace period. PERPANJANG SEKARANG!`;
    bannerConfig.showDismiss = false; // Can't dismiss critical warnings
  } else if (status === 'blocked') {
    // This should not happen as user can't login, but just in case
    bannerConfig.bgColor = '#fecaca'; // red-200
    bannerConfig.borderColor = '#dc2626'; // red-600
    bannerConfig.textColor = '#991b1b'; // red-800
    bannerConfig.iconColor = '#b91c1c'; // red-700
    bannerConfig.title = '❌ Akun Diblokir';
    bannerConfig.message = 'Langganan Anda telah habis dan melewati grace period. Hubungi admin untuk mengaktifkan kembali.';
    bannerConfig.showDismiss = false;
  }

  return (
    <div 
      style={{ 
        backgroundColor: bannerConfig.bgColor, 
        borderBottomWidth: '3px',
        borderBottomColor: bannerConfig.borderColor 
      }}
      className="px-4 py-3 shadow-sm"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1">
            <AlertCircle style={{ color: bannerConfig.iconColor }} className="h-5 w-5 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p style={{ color: bannerConfig.textColor }} className="text-sm font-bold">
                {bannerConfig.title}
              </p>
              <p style={{ color: bannerConfig.textColor }} className="text-sm mt-1">
                {bannerConfig.message}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  onClick={() => window.location.href = '/payment'}
                  className="inline-flex items-center px-4 py-1.5 text-xs font-semibold rounded bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                >
                  💳 Perpanjang Sekarang
                </button>
                <a
                  href={`https://wa.me/${whatsappNumber.replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-1 text-xs font-medium rounded bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
                >
                  📱 WhatsApp Admin
                </a>
                <a
                  href={`mailto:${billingEmail}`}
                  className="inline-flex items-center px-3 py-1 text-xs font-medium rounded bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
                >
                  📧 Email Billing
                </a>
              </div>
            </div>
          </div>
          {bannerConfig.showDismiss && (
            <button
              onClick={() => setDismissed(true)}
              className={`flex-shrink-0 p-1 rounded hover:bg-white/50 transition-colors ${bannerConfig.textColor}`}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionBanner;
